import { Trophy, Frown, RotateCcw, Swords, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Fenêtre de fin de partie.
 * mode: "local" | "ai" | "online"
 */
export default function GameEndModal({
  open,
  mode = "local",
  youWon = null,
  winnerSide = null,
  opponentName = "",
  rematch = null,
  onRestart,
  onRematch,
  onAcceptRematch,
  onDeclineRematch,
  onLeave,
}) {
  const isWin = youWon === true;
  const isLoss = youWon === false;

  const title = (() => {
    if (mode === "local") return "Partie terminée";
    if (isWin) return "Bravo, tu as gagné !";
    if (isLoss) return "Dommage…";
    return "Partie terminée";
  })();

  const description = (() => {
    if (mode === "local") {
      return winnerSide === "A"
        ? "Les blancs remportent la partie."
        : "Les noirs remportent la partie.";
    }
    if (isWin) return "Belle victoire !";
    if (isLoss) return `${opponentName || "Ton adversaire"} remporte la partie.`;
    return "";
  })();

  const rematchRequestedByYou = rematch?.you ?? false;
  const rematchRequestedByOpponent = rematch?.opponent ?? false;
  const waitingOpponent = mode === "online" && rematchRequestedByYou && !rematchRequestedByOpponent;
  const opponentWantsRematch = mode === "online" && rematchRequestedByOpponent && !rematchRequestedByYou;

  return (
    <Dialog open={open}>
      <DialogContent
        className="bg-[color:var(--surface)] border-[color:var(--border)] text-[color:var(--text-primary)] sm:max-w-md z-[100] [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="game-end-modal"
      >
        <DialogHeader className="items-center text-center space-y-3">
          <div
            className={[
              "w-16 h-16 rounded-full flex items-center justify-center",
              mode === "local"
                ? "bg-[color:var(--primary)]/15 text-[color:var(--primary)]"
                : isWin
                  ? "bg-emerald-500/15 text-emerald-400"
                  : "bg-rose-500/15 text-rose-400",
            ].join(" ")}
          >
            {mode === "local" ? (
              <Trophy size={32} />
            ) : isWin ? (
              <Trophy size={32} />
            ) : (
              <Frown size={32} />
            )}
          </div>
          <DialogTitle className="text-xl font-bold tracking-tight">{title}</DialogTitle>
          <DialogDescription className="text-[color:var(--text-secondary)] text-sm">
            {description}
          </DialogDescription>
        </DialogHeader>

        {opponentWantsRematch && (
          <div
            className="mt-2 p-4 rounded-lg border border-[color:var(--primary)]/40 bg-[color:var(--primary)]/10 text-center"
            data-testid="rematch-offer-banner"
          >
            <p className="text-sm font-medium text-[color:var(--primary)]">
              {opponentName} souhaite une revanche !
            </p>
            <div className="flex gap-2 justify-center mt-3">
              <button
                type="button"
                onClick={onAcceptRematch}
                className="px-4 py-2 rounded-md btn-gold text-xs font-medium flex items-center gap-2"
                data-testid="rematch-accept"
              >
                <Swords size={14} /> Accepter
              </button>
              <button
                type="button"
                onClick={onDeclineRematch}
                className="px-4 py-2 rounded-md border border-[color:var(--border)] text-xs hover:bg-[color:var(--surface-glass)]"
                data-testid="rematch-decline"
              >
                Refuser
              </button>
            </div>
          </div>
        )}

        {waitingOpponent && (
          <p
            className="text-center text-xs text-[color:var(--text-secondary)] flex items-center justify-center gap-2"
            data-testid="rematch-waiting"
          >
            <Loader2 size={14} className="animate-spin" />
            En attente de {opponentName || "l'adversaire"}…
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          {mode === "online" ? (
            <>
              {!opponentWantsRematch && (
                <button
                  type="button"
                  onClick={onRematch}
                  disabled={waitingOpponent}
                  className="flex-1 px-4 py-3 rounded-md btn-gold text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="game-end-rematch"
                >
                  <Swords size={16} /> Revanche
                </button>
              )}
              <button
                type="button"
                onClick={onLeave}
                className="flex-1 px-4 py-3 rounded-md border border-[color:var(--border)] text-sm hover:bg-[color:var(--surface-glass)]"
                data-testid="game-end-leave"
              >
                Retour au salon
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onRestart}
              className="flex-1 px-4 py-3 rounded-md btn-gold text-sm font-medium flex items-center justify-center gap-2"
              data-testid="game-end-restart"
            >
              <RotateCcw size={16} />
              {mode === "ai" ? "Revanche" : "Recommencer"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
